import { createMemo, Show } from 'solid-js';
import ItemStackView from './ItemStackView';
import SearchableItemPicker, { Highlight, type PickerEmptyOption } from './SearchableItemPicker';
import { useTranslation } from '../hooks/useTranslation';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { fromItemStackDto } from '../services/itemRefAdapters';
import { resolveItemDisplayName } from '../services/itemDisplayName';
import { parseSnbt } from '../services/snbtReader';
import { getEnchantmentName, formatEnchantmentLevel } from '../services/enchantmentNames';
import type { ItemStackDto, VillagerOfferDto } from '../api/generated/schemas';
import menuStyles from './StorageSelect.module.css';
import styles from './VillagerOfferPicker.module.css';

/** One offer of a hall, merged across every villager that has it, carrying the cheapest of them. */
export interface VillagerOfferChoice {
    offerKey: string;
    offer: VillagerOfferDto;
    minPrice: number;
    maxPrice: number;
    villagerCount: number;
}

export function useVillagerOfferText() {
    const { t, langCode } = useTranslation();
    const resolver = useCraftingResolver();

    const enchantmentsOf = (item: ItemStackDto, lang: string) =>
        (parseSnbt(item.snbt ?? undefined).ench ?? [])
            .map(enchantment => `${getEnchantmentName(enchantment.id, lang)} ${formatEnchantmentLevel(enchantment.lvl)}`);

    const itemLabel = (item: ItemStackDto) => {
        const name = resolveItemDisplayName({ itemId: item.itemId, damage: item.metadata, snbt: item.snbt ?? undefined }, resolver());
        const enchantments = enchantmentsOf(item, langCode());
        return enchantments.length > 0 ? `${name} (${enchantments.join(', ')})` : name;
    };

    // English enchantment names stay searchable in every language: "power" finds a German "Stärke V" book.
    const searchText = (choice: VillagerOfferChoice) => [
        itemLabel(choice.offer.sell),
        ...enchantmentsOf(choice.offer.sell, 'en'),
        itemLabel(choice.offer.buyFirst),
        choice.offer.buySecond ? itemLabel(choice.offer.buySecond) : '',
    ].join(' ').toLowerCase();

    const priceLabel = (choice: VillagerOfferChoice) => choice.minPrice === choice.maxPrice
        ? t('VILLAGER_TRADING_OFFER_PRICE', String(choice.minPrice))
        : t('VILLAGER_TRADING_OFFER_PRICE', t('VILLAGER_TRADING_PRICE_RANGE', String(choice.minPrice), String(choice.maxPrice)));

    const stackSizeOf = (item: ItemStackDto) =>
        (resolver()?.getItemById(item.itemId, item.metadata) ?? resolver()?.getItemById(item.itemId, 0))?.stackSize ?? 64;

    return { itemLabel, searchText, priceLabel, stackSizeOf };
}

export function VillagerOfferIcons(props: { offer: VillagerOfferDto; size?: number }) {
    const size = () => props.size ?? 28;
    return (
        <span class={styles.icons}>
            <ItemStackView stack={fromItemStackDto(props.offer.buyFirst)} size={size()} showTooltip />
            <Show when={props.offer.buySecond}>
                {(second) => <><span class={styles.joiner}>+</span><ItemStackView stack={fromItemStackDto(second())} size={size()} showTooltip /></>}
            </Show>
            <i class={`fa-solid fa-arrow-right ${styles.joiner}`} aria-hidden="true" />
            <ItemStackView stack={fromItemStackDto(props.offer.sell)} size={size()} showTooltip />
        </span>
    );
}

export default function VillagerOfferPicker(props: {
    choices: VillagerOfferChoice[];
    value: string | null;
    onChange: (offerKey: string | null) => void;
    placeholder: string;
    emptyOption?: PickerEmptyOption;
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const text = useVillagerOfferText();

    const choicesBySellName = createMemo(() =>
        [...props.choices].sort((left, right) =>
            text.itemLabel(left.offer.sell).localeCompare(text.itemLabel(right.offer.sell)) || left.minPrice - right.minPrice));

    return (
        <SearchableItemPicker
            choices={choicesBySellName()}
            keyOf={choice => choice.offerKey}
            searchTextOf={text.searchText}
            value={props.value ?? ''}
            onChange={offerKey => props.onChange(offerKey === '' ? null : offerKey)}
            placeholder={props.placeholder}
            searchPlaceholder={t('VILLAGER_TRADING_SEARCH_OFFERS')}
            noMatchKey="VILLAGER_TRADING_NO_OFFER_MATCH"
            emptyOption={props.emptyOption}
            disabled={props.disabled}
            renderSelected={choice => (
                <>
                    <VillagerOfferIcons offer={choice.offer} size={22} />
                    <span class={menuStyles.name}>{text.itemLabel(choice.offer.sell)}</span>
                </>
            )}
            renderOption={(choice, query) => (
                <>
                    <span class={styles.result}><ItemStackView stack={fromItemStackDto(choice.offer.sell)} size={30} /></span>
                    <span class={menuStyles.body}>
                        <span class={menuStyles.lineTop}>
                            <span class={menuStyles.name}><Highlight text={text.itemLabel(choice.offer.sell)} query={query()} /></span>
                            <span class={menuStyles.stats}>{text.priceLabel(choice)}</span>
                        </span>
                        <span class={menuStyles.lineBottom}>
                            <VillagerOfferIcons offer={choice.offer} size={20} />
                            <span class={menuStyles.stats}>{t('VILLAGER_TRADING_OFFER_VILLAGERS', String(choice.villagerCount))}</span>
                        </span>
                    </span>
                </>
            )}
        />
    );
}
